import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { colors } from "../Shared/colors";
import { Toaster, toast } from "react-hot-toast";
import { useLoginMutation } from "../features/authSlice";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import { Loader } from "../Shared/Loader";
import { logo } from "../Shared/config";
import { encryptData } from "../services/crypto";
import { CircularProgress } from "@mui/material";

export const LoginComponent = () => {
  const [visibility, setVisibility] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(false);

  const [loginUser] = useLoginMutation();
  const navigate = useNavigate();

  const [cred, setCred] = useState({
    email: "",
    password: "",
  });

  // Load remembered email if exists
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      try {
        const email = JSON.parse(rememberedEmail);
        setCred((prev) => ({ ...prev, email }));
        setRememberMe(true);
      } catch (error) {
        console.error("Failed to load remembered email:", error);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCred((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = { email: "", password: "" };
    let isValid = true;

    if (!cred.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(cred.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!cred.password.trim()) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (cred.password.trim().length < 6) {
      newErrors.password = "Password must be at least 6 characters";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoader(true);

    const credentials = {
      email: cred.email.trim(),
      password: cred.password,
    };

    try {
      const result = await loginUser(credentials).unwrap();
      toast.success("Login successful");

      const storeEncryptedData = (key, value) =>
        localStorage.setItem(key, encryptData(value));

      localStorage.setItem("token", result.token);
      storeEncryptedData("id", result.user._id);
      storeEncryptedData("staffName", `${result.user.firstName} ${result.user.lastName}`);
      storeEncryptedData("role", result.user.role);
      storeEncryptedData("email", result.user.email);
      storeEncryptedData("user", result.user);

      // Handle remember me
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", JSON.stringify(cred.email.trim()));
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      // ✅ Mark new login
      sessionStorage.setItem("justLoggedIn", "true");

      // ✅ Wait briefly so RequireAuth can reinitialize cleanly
      setTimeout(() => {
        const role = result.user.role?.toLowerCase();
        const redirectPath = role === "ts" ? "/telesale" : "/";

        navigate(redirectPath, { replace: true });
      }, 300);
    } catch (error) {
      const errorMessage = error?.data?.error?.message || "Login failed. Please check your credentials.";
      toast.error(errorMessage);
      
      // Set field-specific errors if available
      if (errorMessage.toLowerCase().includes("email")) {
        setErrors((prev) => ({ ...prev, email: errorMessage }));
      } else if (errorMessage.toLowerCase().includes("password")) {
        setErrors((prev) => ({ ...prev, password: errorMessage }));
      }
    } finally {
      setLoader(false);
      // Only clear password on success, keep email for retry
      setCred((prev) => ({ ...prev, password: "" }));
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const isFormValid =
    /\S+@\S+\.\S+/.test(cred.email) && cred.password.trim() !== "";

  return (
    <Main>
      {loader && <Loader />}
      <BackgroundGradient />
      <TopSphere />
      <BottomSphere />
      <Container>
        <Logo>
          <img src={logo} alt="Company Logo" />
        </Logo>
        <WelcomeSection>
          <WelcomeTitle>Welcome Back</WelcomeTitle>
          <WelcomeSubtitle>Sign in to continue to your account</WelcomeSubtitle>
        </WelcomeSection>
        <Form onSubmit={handleLogin}>
          <InputGroup>
            <InputContainer hasError={!!errors.email}>
              <InputIcon>
                <MailOutlineIcon />
              </InputIcon>
              <StyledInput
                type="email"
                name="email"
                id="email"
                placeholder=" "
                value={cred.email}
                onChange={handleChange}
                required
                autoComplete="email"
                aria-label="Email address"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              <InputLabel htmlFor="email">Email Address</InputLabel>
            </InputContainer>
            {errors.email && <ErrorMessage id="email-error">{errors.email}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <InputContainer hasError={!!errors.password}>
              <InputIcon>
                <LockOutlinedIcon />
              </InputIcon>
              <StyledInput
                type={visibility ? "text" : "password"}
                name="password"
                id="password"
                placeholder=" "
                value={cred.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                aria-label="Password"
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? "password-error" : undefined}
              />
              <InputLabel htmlFor="password">Password</InputLabel>
              <PasswordToggle
                type="button"
                onClick={() => setVisibility(!visibility)}
                aria-label={visibility ? "Hide password" : "Show password"}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setVisibility(!visibility);
                  }
                }}
              >
                {visibility ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </PasswordToggle>
            </InputContainer>
            {errors.password && <ErrorMessage id="password-error">{errors.password}</ErrorMessage>}
          </InputGroup>

          <OptionsRow>
            <CheckBox>
              <CheckboxInput
                type="checkbox"
                name="remember"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <CheckboxLabel htmlFor="remember">Remember me</CheckboxLabel>
            </CheckBox>
          </OptionsRow>

          <SubmitButton type="submit" disabled={!isFormValid || loader}>
            {loader ? (
              <>
                <CircularProgress size={20} style={{ color: colors.white, marginRight: "0.5rem" }} />
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </SubmitButton>
        </Form>
      </Container>
      <Toaster position="top-center" />
    </Main>
  );
};

// Animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Main = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 50%, #e0e7ff 100%);
  background-size: 400% 400%;
  animation: ${gradientShift} 20s ease infinite;
`;

const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(36, 147, 147, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(36, 147, 147, 0.06) 0%, transparent 50%),
    radial-gradient(circle at 40% 20%, rgba(36, 147, 147, 0.05) 0%, transparent 50%);
  pointer-events: none;
`;

const TopSphere = styled.div`
  background: linear-gradient(135deg, rgba(36, 147, 147, 0.15) 0%, rgba(26, 107, 107, 0.1) 100%);
  width: 500px;
  height: 500px;
  border-radius: 50%;
  position: absolute;
  top: -200px;
  right: -200px;
  filter: blur(80px);
  animation: ${float} 12s ease-in-out infinite;
  z-index: 0;
`;

const BottomSphere = styled.div`
  background: linear-gradient(135deg, rgba(36, 147, 147, 0.12) 0%, rgba(26, 107, 107, 0.08) 100%);
  width: 500px;
  height: 500px;
  border-radius: 50%;
  position: absolute;
  left: -200px;
  bottom: -200px;
  filter: blur(80px);
  animation: ${float} 14s ease-in-out infinite reverse;
  z-index: 0;
`;

const Container = styled.div`
  width: 90%;
  max-width: 450px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.5);
  border-radius: 24px;
  padding: 3rem 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  position: relative;
  z-index: 1;
  animation: ${slideIn} 0.6s ease-out;

  @media (max-width: 768px) {
    width: 95%;
    padding: 2.5rem 2rem;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }
`;

const Logo = styled.div`
  width: 120px;
  height: 120px;
  margin-bottom: 0.5rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: 0.5rem;
`;

const WelcomeTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${colors.text};
  margin: 0 0 0.5rem 0;
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: 0.95rem;
  color: ${colors.textLight};
  margin: 0;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
  background: ${colors.white};
  border: 2px solid ${props => props.hasError ? '#ef4444' : colors.inputBorderWhite};
  border-radius: 12px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;

  &:focus-within {
    border-color: ${props => props.hasError ? '#ef4444' : colors.brown};
    box-shadow: 0 0 0 4px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(36, 147, 147, 0.1)'};
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.inputFeild};
  z-index: 1;
  transition: color 0.3s ease;

  ${InputContainer}:focus-within & {
    color: ${colors.brown};
  }

  svg {
    font-size: 1.25rem;
  }
`;

const InputLabel = styled.label`
  position: absolute;
  left: 3rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.inputFeild};
  font-size: 1rem;
  pointer-events: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: transparent;
  z-index: 1;
  white-space: nowrap;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  color: ${colors.text};
  transition: all 0.3s ease;

  &::placeholder {
    color: transparent;
  }

  &:focus + ${InputLabel}, 
  &:not(:placeholder-shown) + ${InputLabel},
  &[value]:not([value=""]) + ${InputLabel} {
    top: 0;
    left: 0.75rem;
    transform: translateY(-50%) scale(0.85);
    color: ${colors.brown};
    background: ${colors.white};
    padding: 0 0.5rem;
    font-weight: 500;
  }

  &:-webkit-autofill + ${InputLabel} {
    top: 0;
    left: 0.75rem;
    transform: translateY(-50%) scale(0.85);
    color: ${colors.brown};
    background: ${colors.white};
    padding: 0 0.5rem;
    font-weight: 500;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px ${colors.white} inset;
    -webkit-text-fill-color: ${colors.text};
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.inputFeild};
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  z-index: 1;

  &:hover {
    background: ${colors.lightWhite};
    color: ${colors.brown};
  }

  &:focus {
    outline: 2px solid ${colors.brown};
    outline-offset: 2px;
  }

  svg {
    font-size: 1.25rem;
  }
`;

const ErrorMessage = styled.span`
  font-size: 0.875rem;
  color: #ef4444;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  animation: ${slideIn} 0.3s ease-out;

  &::before {
    content: "⚠";
    font-size: 0.875rem;
  }
`;

const OptionsRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: -0.5rem;
`;

const CheckBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${colors.brown};
  border-radius: 4px;

  &:focus {
    outline: 2px solid ${colors.brown};
    outline-offset: 2px;
  }
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  color: ${colors.textMed};
  cursor: pointer;
  user-select: none;
  font-weight: 400;
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.text};
  }
`;


const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  color: ${colors.white};
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(36, 147, 147, 0.3);
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(36, 147, 147, 0.4);
    background: linear-gradient(135deg, #1a6b6b 0%, ${colors.brown} 100%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:focus {
    outline: 2px solid ${colors.brown};
    outline-offset: 2px;
  }
`;